import {
  H1,
  isWeb,
  SizableText,
  Stack,
  styled,
  Unspaced,
  XStack,
  YStack,
} from '@mythical/ui'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { Button, ButtonLink } from 'app/components/primitive/Button'
import { IconHeart } from 'app/components/primitive/Icon'
import { IconArrow } from 'app/components/primitive/Icon/icons/IconArrow'
import { IconShare } from 'app/components/primitive/Icon/icons/IconShare'
import config from 'app/config'
import { useStores } from 'app/store'
import React from 'react'
import { Link, LinkCore } from 'solito/link'

import { IconHeartFull } from '../primitive/Icon/icons/IconHeartFull'
import Spinner from '../primitive/Spinner'
import ContentTierBadge, { ContentTierBadgeProps } from './ContentTierBadge'
import ShareModal from './ShareModal/ShareModal'

type ContentInfoProps = {
  category?: string
  categoryHref?: string
  datePublished?: string
  commentCount?: number
  likeCount?: number
  hasLiked?: boolean
  likeLoading?: boolean
  isVideo?: boolean
  title: string
  slug: string
  tier: ContentTierBadgeProps['tier']
  tierSlug: string
  tags: {
    label: string
    href: string
  }[]
  onLike?: () => void
  disableComments?: boolean
  series?: string
}

const ContentInfoWrapper = styled(Stack, {
  width: '100%',
  gap: '$3',
  fd: 'column',
  $gtXs: {
    fd: 'row',
    ai: 'flex-start',
  },
})

const ActionWrapper = styled(Stack, {
  gap: '$2',
  fd: 'row',
  flexWrap: 'wrap',
  ai: 'flex-start',
  $gtXs: {
    minWidth: 200,
    jc: 'flex-end',
    flexShrink: 1,
  },
})

const ContentInfo = ({
  category,
  categoryHref,
  datePublished,
  commentCount,
  likeCount = 0,
  hasLiked = false,
  likeLoading = false,
  tier,
  tierSlug,
  title,
  tags,
  slug,
  onLike,
  disableComments,
  series,
}: ContentInfoProps) => {
  const { userStore } = useStores()
  const loggedIn = !!userStore?.user?.id
  const seriesSlug = series ? `series/${series}/${slug}` : `content/${slug}`
  return (
    <ContentInfoWrapper>
      <YStack w="100%" flexShrink={1} space="$2">
        <XStack
          spaceDirection="both"
          space="$3"
          alignItems="center"
          flexWrap="wrap"
        >
          <Unspaced>
            <ContentTierBadge
              width={40}
              height={40}
              tier={tier}
              href={`/tier/${tierSlug}`}
              mr="$1.5"
            />
          </Unspaced>
          {category && categoryHref && (
            <LinkCore
              href={`/collection/${categoryHref}`}
              Component={Tag}
              componentProps={{
                tag: 'a',
                my: '$1.5',
                displaySize: 'md',
                variant: 'secondary',
              }}
            >
              {category}
            </LinkCore>
          )}
          <SizableText
            textTransform="uppercase"
            fontSize="$2"
            color="$secondary6"
            my="$1.5"
          >
            {datePublished}
            {!disableComments && (
              <>
                •{' '}
                <SizableText
                  textTransform="uppercase"
                  fontSize="$2"
                  color="$secondary7"
                  my="$1.5"
                >
                  {commentCount} Comments
                </SizableText>
              </>
            )}
          </SizableText>
        </XStack>
        <H1
          textTransform="uppercase"
          size="$8"
          color="$primary1"
          fontWeight="700"
          $gtMd={{
            size: '$9',
            fontWeight: '700',
          }}
          $gtLg={{
            size: '$10',
            fontWeight: '700',
          }}
        >
          {title}
        </H1>
        {tags.length > 0 && (
          <XStack
            w="100%"
            jc="flex-start"
            flexShrink={1}
            ai="flex-start"
            space="$2"
          >
            <SizableText fontSize="$4" color="$primary1">
              Tags:
            </SizableText>
            <XStack w="100%" space="$2" flexShrink={1} flexWrap="wrap">
              {tags.map((tag) => (
                <Link href={tag.href} key={tag.href}>
                  <SizableText
                    fontSize="$4"
                    color="$primary6"
                    textDecorationLine="underline"
                    hoverStyle={{
                      color: '$primary4',
                    }}
                  >
                    {tag.label}
                  </SizableText>
                </Link>
              ))}
            </XStack>
          </XStack>
        )}
      </YStack>
      <ActionWrapper>
        {loggedIn && (
          <Button
            scaleSpace={0.4}
            p="$3"
            theme="tertiary"
            icon={
              likeLoading ? (
                <Spinner px="$1" scale={1} />
              ) : hasLiked ? (
                <IconHeartFull size={20} color="$primary6" />
              ) : (
                <IconHeart size={20} color="$primary6" />
              )
            }
            onPress={onLike}
          >
            {`${likeCount}`}
          </Button>
        )}

        <ShareModal
          href={`${config.siteUrl}/${seriesSlug}`}
          trigger={
            <Button
              p="$3"
              theme="tertiary"
              icon={<IconShare size={20} color="$primary6" />}
            >
              Share via
            </Button>
          }
        />
        {isWeb && !disableComments && (
          <ButtonLink
            href="#comments"
            scaleSpace={0.4}
            p="$3"
            theme="tertiary"
            iconAfter={<IconArrow size={20} color="$primary6" />}
          >{`${commentCount} Comments`}</ButtonLink>
        )}
      </ActionWrapper>
    </ContentInfoWrapper>
  )
}

export default ContentInfo
